import { Button } from '@/components/ui/button';
import { SEOProps } from '@/components/ui/SEO';
import { UnderlinedText } from '@/components/ui/underline-text';
import { basePath, withBasePath } from '@/utils/basePath';
import {
  STATUTS_LIBRES,
  STATUTS_OBLIGATOIRES,
  STATUTS_RECOMMANDES,
  SUMMARY_ITEMS,
} from '@/utils/data/facturationContent';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from 'next/image';
import { useMemo, useState } from 'react';

gsap.registerPlugin(ScrollTrigger);

const FACTURATION_ELECTRONIQUE_SEO: SEOProps = {
  title: 'Facturation électronique 2026 : obligations et calendrier | zLawyer',
  description:
    'Comprenez la réforme de la facturation électronique obligatoire dès 2026 : e-invoicing, e-reporting, formats Factur-X, obligations et calendrier pour les cabinets d’avocats.',
  keywords:
    'facturation électronique, réforme 2026, e-invoicing, e-reporting, Factur-X, cabinet avocat, obligations TVA',
  url: `https://www.zlawyer.fr${basePath}/facturation-electronique`,
};

type Statut = {
  nom: string;
  role: string;
  declencheur: string;
  utilite: string;
};

function BulletItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="leading-relaxed">
      <span className="text-orange mt-1 mr-3">•</span>
      {children}
    </li>
  );
}

function StatusSection({
  title,
  items,
  description,
}: {
  title: string;
  items: Statut[];
  description: string;
}) {
  return (
    <div className="mb-10">
      <h4 className="text-orange mb-2 text-lg font-bold">{title}</h4>
      <p className="mb-4">{description}</p>

      <div className="hidden md:block">
        <table className="min-w-full border-collapse overflow-hidden rounded-lg bg-white shadow">
          <thead>
            <tr>
              <th className="bg-gray-100 p-4 text-left text-xs font-bold uppercase">Statut</th>
              <th className="bg-gray-100 p-4 text-left text-xs font-bold uppercase">Rôle</th>
              <th className="bg-gray-100 p-4 text-left text-xs font-bold uppercase">
                Qui le déclenche ?
              </th>
              <th className="bg-gray-100 p-4 text-left text-xs font-bold uppercase">
                Utilité métier
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((statut, index) => (
              <tr
                key={`${title}-${statut.nom}`}
                className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
              >
                <td className="rounded-l-lg border-b border-gray-200 bg-orange-50 p-4 align-middle font-bold text-orange-700">
                  {statut.nom}
                </td>
                <td className="border-b border-gray-200 p-4 align-middle text-gray-800">
                  {statut.role}
                </td>
                <td className="border-b border-gray-200 p-4 align-middle text-gray-800">
                  {statut.declencheur}
                </td>
                <td className="rounded-r-lg border-b border-gray-200 p-4 align-middle text-gray-800">
                  {statut.utilite}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-4 md:hidden">
        {items.map((statut) => (
          <div
            key={`${title}-mobile-${statut.nom}`}
            className="border-orange rounded-lg border-l-4 bg-white p-4 shadow"
          >
            <div className="mb-2 font-bold text-orange-700">{statut.nom}</div>
            <div className="mb-1 text-sm">
              <span className="font-semibold text-gray-900">Rôle :</span> {statut.role}
            </div>
            <div className="mb-1 text-sm">
              <span className="font-semibold text-gray-900">Qui le déclenche ?</span>{' '}
              {statut.declencheur}
            </div>
            <div className="text-sm">
              <span className="font-semibold text-gray-900">Utilité métier :</span> {statut.utilite}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FacturationElectronique() {
  const [activeSection, setActiveSection] = useState('contexte');
  const summaryItems = useMemo(() => SUMMARY_ITEMS, []);
  const sections = Array.from(document.querySelectorAll<HTMLElement>('section[id]'));

  useGSAP(() => {
    if (!sections.length) return;

    const triggers = sections.map((section) =>
      ScrollTrigger.create({
        trigger: section,
        start: 'top 250px',
        end: 'bottom center',
        onEnter: () => setActiveSection(section.id),
        onEnterBack: () => setActiveSection(section.id),
      }),
    );

    ScrollTrigger.refresh();

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="hero max-width-blue-section relative">
      <div className="grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-left">
            La Facture Électronique Obligatoire: Ce qu&apos;il faut savoir
          </h1>
          <p>
            Tout ce que vous devez savoir sur la future réforme fiscale liée à la facturation
            électronique en France : quels changements attendre et comment s’y préparer.
          </p>
        </div>
        <Image
          alt="illustration du logiciel"
          className="w-full rounded-(--radius-current)"
          height={600}
          src={withBasePath('/images/features/features10.webp')}
          width={900}
        />
      </div>

      <div className="mt-32 flex w-full gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-32 left-0 w-64 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-orange mb-4 text-lg font-semibold">Sommaire</h3>
            <ul className="space-y-3">
              {summaryItems.map((item) => {
                const isActive = activeSection === item.href.slice(1);

                return (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      className={`hover:text-orange block py-1 text-sm transition-colors duration-200 ${
                        isActive ? 'text-orange font-bold' : ''
                      }`}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <div className="w-full">
          <section className="scroll-mt-32 pb-16" id="contexte">
            <h2 className="mb-6 text-3xl font-bold">
              <UnderlinedText>Contexte</UnderlinedText>
            </h2>
            <p className="leading-relaxed">
              Dès 2026, la réforme sur la facturation électronique entre en vigueur pour toutes les
              entreprises françaises.
              <br />
              Conformément à l’article 289 du Code général des impôts, la facture électronique est
              un document entièrement numérique, couvrant toutes les étapes de son cycle de vie, de
              l’émission à la réception. Elle doit respecter un format et un mode de transmission
              définis par l’administration fiscale, afin de permettre un traitement automatisé et
              sécurisé. Les informations obligatoires d’une facture classique restent exigées,
              assurant ainsi la valeur juridique du document.
              <br />
              Objectif : moderniser, sécuriser et automatiser les échanges de factures dans un cadre
              réglementaire strict.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="definition">
            <h2 className="mb-6 text-3xl font-bold">
              La facture électronique{' '}
              <UnderlinedText>c&apos;est quoi au juste ?</UnderlinedText>
            </h2>
            <p className="mb-6 leading-relaxed">
              Une <span className="font-bold">facture électronique</span> est une facture créée,
              transmise, reçue et archivée{' '}
              <span className="font-bold">de manière entièrement numérique</span>, sans intervention
              papier, dont les données respectent une structure particulière et un mode de
              transmission normé par l&apos;administration fiscale, permettant un traitement
              automatisé et électronique.
            </p>
            <p className="mb-6 leading-relaxed">
              Il existe <span className="font-bold">deux formats officiels</span> autorisés :
            </p>
            <ul className="space-y-3">
              <li className="leading-relaxed">
                <span className="text-orange mt-1 mr-3">•</span>
                <span className="font-bold">Factur-X</span> : format hybride contenant à la fois un
                PDF lisible et des données XML lisibles par les systèmes. Ce format a pour
                particularité, et avantage, d’être autant lisible par l’humain, que par l’ordinateur
                et s’annonce comme{' '}
                <span className="font-bold">le format le plus utilisé à l’avenir</span>. Il est
                délivré via un fichier PDF (normé PDF/A-3) : celui-ci comprend des informations
                lisibles ainsi que les données structurées et d’éventuelles pièces jointes.
              </li>
              <li className="leading-relaxed">
                <span className="text-orange mt-1 mr-3">•</span>
                <span className="font-bold">UBL ou CII</span> : formats structurés XML lisibles
                uniquement par les logiciels mais non lisibles par l&apos;humain. Ces formats seront
                vraisemblablement peu, voire pas utilisés par les TPE / PME.
              </li>
            </ul>
            <p className="my-6 leading-relaxed">
              Contrairement à un simple PDF envoyé par email, la facture électronique suit un{' '}
              <span className="font-bold">processus normé</span>, avec transmission via une{' '}
              <span className="font-bold">plateforme agréée</span> et un{' '}
              <span className="font-bold">suivi de statut réglementaire</span>.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="concernes">
            <h2 className="mb-6 text-3xl font-bold">
              <UnderlinedText>Qui est concerné</UnderlinedText> ?
            </h2>
            <p className="leading-relaxed">
              La réforme s&apos;applique à toutes les entreprises assujetties à la TVA, sans
              distinction de taille ou de secteur, y compris les cabinets d&apos;avocats. Elle
              concerne aussi bien les émetteurs que les destinataires de factures.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="reforme">
            <h2 className="mb-6 text-3xl font-bold">
              <UnderlinedText>Que prévoit la réforme</UnderlinedText> de la facture
              électronique ?
            </h2>
            <p className="mb-6 leading-relaxed">
              Elle vise à généraliser les échanges de factures électroniques entre entreprises
              assujetties à la TVA. Elle repose sur deux volets principaux à prendre en compte :
            </p>
            <ul className="space-y-3">
              <BulletItem>E-invoicing pour les échanges B2B</BulletItem>
              <BulletItem>E-reporting pour les opérations B2C ou internationales</BulletItem>
            </ul>
            <p className="mt-6 leading-relaxed">
              Les échanges de factures électronique se font via une Plateforme Agrée (PA) connectée
              à zLawyer ou autre solution métier.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="plateforme-agre">
            <h2 className="mb-6 text-3xl font-bold">
              Qu&apos;est-ce qu&apos;une <UnderlinedText>Plateforme Agrée</UnderlinedText> ?
            </h2>
            <p className="mb-6 leading-relaxed">
              La <span className="font-bold">Plateforme Agréée</span> (anciennement appelée PDP –
              Plateforme de Dématérialisation Partenaire) constitue l’élément central du dispositif.
              Elle garantit la{' '}
              <span className="font-bold">conformité et la sécurité des échanges</span> de factures,
              tout en facilitant la gestion quotidienne des entreprises.
            </p>
            <p className="mb-6 leading-relaxed">
              Contrairement à un logiciel de facturation, la plateforme agréée est une
              infrastructure technique qui assure :
            </p>
            <ul className="space-y-3">
              <BulletItem>
                Le respect des formats normalisés pour les factures électroniques (UBL, CII,
                Factur-X);
              </BulletItem>
              <BulletItem>
                La circulation fluide et interopérable des factures entre toutes les plateformes
                agréées, afin que chaque destinataire puisse les recevoir quelle que soit sa
                solution ;
              </BulletItem>
              <BulletItem>
                La transmission automatique à l’administration fiscale des données nécessaires au
                suivi de la TVA et au e-reporting ;
              </BulletItem>
              <BulletItem>
                L’archivage sécurisé des factures, garantissant leur intégrité et leur accessibilité
                dans le temps.
              </BulletItem>
            </ul>
            <p className="mt-6 leading-relaxed">
              Toutes les plateformes agréées sont immatriculées par la DGFiP, qui veille à ce que
              chaque facture électronique soit émise, reçue et transmise conformément aux normes
              légales en vigueur.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="e-invoicing">
            <h2 className="mb-6 text-3xl font-bold">
              Qu&apos;est-ce que l&apos;<UnderlinedText>E-invoicing</UnderlinedText> ?
            </h2>
            <p className="mb-6 leading-relaxed">
              L&apos;<span className="font-bold">e-invoicing</span> s&apos;applique aux{' '}
              <span className="font-bold">transactions B2B domestiques</span> (entre entreprises
              françaises soumises à la TVA).
            </p>
            <p className="mb-6 leading-relaxed">
              Les factures doivent être émises et reçues via une{' '}
              <span className="font-bold">Plateforme Agréée Partenaire (PA)</span>, dans un{' '}
              <span className="font-bold">format électronique structuré ou hybride</span>.
            </p>
            <p className="mb-6 leading-relaxed">
              Les Plateformes de Dématérialisation Partenaire (PA) jouent donc un rôle central dans
              cette réforme en assurant la conformité et la sécurisation des échanges de factures
              électroniques.
            </p>
            <div className="border-orange mt-6 rounded border-l-4 bg-orange-50 p-4">
              <p className="font-medium text-orange-800">
                Important : l&apos;envoi de factures en simple PDF ne sera plus conforme à partir de
                2026.
              </p>
            </div>
            <p className="my-6 leading-relaxed">
              Assurez votre conformité à la réforme fiscale sur la facturation électronique en
              optant pour la solution de zLawyer. <br />
              Ainsi, vous bénéficiez d’une solution fluide et continue grâce à une interface unique.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="e-reporting">
            <h2 className="mb-6 text-3xl font-bold">
              Qu&apos;est-ce que l&apos;<UnderlinedText>E-reporting</UnderlinedText> ?
            </h2>
            <p className="mb-6 leading-relaxed">
              <span className="font-bold">E-reporting</span> : concerne{' '}
              <span className="font-bold">les transactions hors champ du e-invoicing</span>, mais
              pour lesquelles l’administration veut{' '}
              <span className="font-bold">suivre la TVA et les flux économiques</span>. On envoie{' '}
              <span className="font-bold">un relevé de transaction</span>, pas la facture.
            </p>
            <p className="mb-3 leading-relaxed">Les transactions concernées sont :</p>
            <ul className="space-y-3">
              <li className="leading-relaxed">
                <span className="text-orange mt-1 mr-3">•</span>
                Transactions <span className="font-bold">B2C</span> (avec des particuliers).
                <br />
                Exemple : une entreprise française vend à un consommateur final en France ou à
                l’étranger.
              </li>
              <li className="leading-relaxed">
                <span className="text-orange mt-1 mr-3">•</span>
                Transactions <span className="font-bold">B2B internationales.</span>
              </li>
              <li className="leading-relaxed">
                <span className="text-orange mt-1 mr-3">•</span>
                <span className="font-bold">Acquisitions intracommunautaires.</span>
                <br />
                Exemple : une entreprise française vend ou achète à une entreprise en Allemagne, aux
                États-Unis, etc.
              </li>
              <li className="leading-relaxed">
                <span className="text-orange mt-1 mr-3">•</span>
                <span className="font-bold">Services rendus hors UE.</span>
              </li>
              <li className="leading-relaxed">
                <span className="text-orange mt-1 mr-3">•</span>
                Les <span className="font-bold">encaissements</span> soumis à TVA.
              </li>
            </ul>

            <p className="mt-6 leading-relaxed">
              L’entreprise envoie régulièrement (périodicité imposée : quotidien, hebdo ou mensuel
              selon les cas) des <span className="font-bold">données de transactions</span> à
              l’administration fiscale.
            </p>
            <div className="pt-4">
              <p className="leading-relaxed">Cela peut passer par :</p>
              <ul className="space-y-2 pt-4 pl-4">
                <li className="leading-relaxed">
                  <span className="mt-1 mr-1 inline-block h-1.5 w-1.5 rounded-full border border-black text-white" />
                  <span className="font-bold">Le Portail Public de Facturation (PPF)</span>;
                </li>
                <li className="leading-relaxed">
                  <span className="mt-1 mr-1 inline-block h-1.5 w-1.5 rounded-full border border-black text-white" />
                  Ou une <span className="font-bold">Plateforme Agrée (PA)</span> connectée à
                  zLawyer ou autre solution métier.
                </li>
              </ul>
            </div>

            <p className="my-6 leading-relaxed">
              Pour les cabinets d’avocats, l’e-reporting via zLawyer permet de rester en conformité
              avec les nouvelles obligations fiscales tout en assurant la traçabilité et la
              transparence des opérations.
            </p>
            <p className="mb-6 leading-relaxed">
              <span className="font-bold">Le e-reporting suivra le même calendrier</span> que celui
              de la facture électronique, via leur plateforme.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="calendrier">
            <h2 className="mb-6 text-3xl font-bold">
              <UnderlinedText>Calendrier</UnderlinedText> de mise en œuvre
            </h2>
            <p className="mb-8 leading-relaxed">
              La réforme de la facturation électronique prévoit une mise en place progressive avec
              des échéances précises. Il est indispensable de respecter les délais légaux afin
              d’éviter toute sanction.
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex min-w-48 flex-col items-center rounded-lg border-2 border-orange-200 bg-white p-4 text-center text-lg font-bold shadow-sm">
                  <span>1er</span>
                  <span>Septembre</span>
                  <span>2026</span>
                </div>
                <div className="ml-6 flex-1">
                  <h4 className="mb-2 font-semibold text-gray-800">Phase 1 :</h4>
                  <ul className="space-y-2">
                    <BulletItem>
                      Obligation de{' '}
                      <span className="font-bold">
                        recevoir des factures électroniques pour TOUTES les entreprises
                      </span>
                    </BulletItem>
                    <BulletItem>
                      <span className="font-bold">Grandes entreprises et ETI</span> : Obligation
                      d&apos;émettre des factures électroniques
                    </BulletItem>
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-400">
                  <svg
                    className="h-4 w-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex min-w-48 flex-col items-center rounded-lg border-2 border-orange-200 bg-white p-4 text-center text-lg font-bold shadow-sm">
                  <span>1er</span>
                  <span>Septembre</span>
                  <span>2027</span>
                </div>
                <div className="ml-6 flex-1">
                  <h4 className="mb-2 font-semibold text-gray-800">
                    Phase 2 : PME, TPE et microentreprises
                  </h4>
                  <ul className="space-y-2">
                    <BulletItem>
                      Obligation d&apos;émettre des factures électroniques pour les PME, TPE et
                      microentreprises
                    </BulletItem>
                    <BulletItem>Généralisation complète de la facturation électronique</BulletItem>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-lg border-l-4 border-orange-400 bg-orange-50 p-6">
              <p className="font-medium text-orange-800">
                ⏰ Préparez-vous dès maintenant pour être conforme à l&apos;échéance qui vous
                concerne grâce à zLawyer. <br />
                <Button className="mt-3 underline" href="/contact" variant="transparent">
                  En savoir +
                </Button>
              </p>
            </div>
          </section>

          <section className="scroll-mt-32 pb-16" id="obligations-des-entreprises">
            <h2 className="mb-6 text-3xl font-bold">
              <UnderlinedText>Obligations</UnderlinedText> des entreprises
            </h2>
            <ul className="space-y-3">
              <BulletItem>
                Utiliser une plateforme agréée (PA) pour l&apos;émission et la réception des
                factures,
              </BulletItem>
              <BulletItem>Respecter les formats imposés (UBL, CII ou Factur-X),</BulletItem>
              <BulletItem>
                Assurer la traçabilité, le cycle de vie et le reporting des factures.
              </BulletItem>
            </ul>
          </section>

          <section className="scroll-mt-32 pb-16" id="avantages-de-la-reforme">
            <h2 className="mb-6 text-3xl font-bold">
              <UnderlinedText>Avantages</UnderlinedText> de la réforme
            </h2>
            <ul className="space-y-3">
              <BulletItem>
                Réduction des délais de paiement, en facilitant le traitement et la validation des
                factures,
              </BulletItem>
              <BulletItem>
                Réduction des coûts liés au papier, à l’impression et à l’acheminement,
              </BulletItem>
              <BulletItem>Automatisation et fiabilisation des processus comptables,</BulletItem>
              <BulletItem>
                Lutte contre la fraude à la TVA, en permettant un meilleur contrôle par
                l’administration fiscale,
              </BulletItem>
              <BulletItem>
                Renforcer la traçabilité et la transparence, grâce à des échanges numériques
                sécurisés et horodatés,
              </BulletItem>
              <BulletItem>
                Harmoniser les pratiques, notamment avec les obligations légales en France et en
                Europe qui généralisent progressivement la facture électronique.
              </BulletItem>
            </ul>
          </section>

          <section className="scroll-mt-32 pb-16" id="mode-operatoire">
            <h2 className="mb-6 text-3xl font-bold">
              Le<UnderlinedText> mode opératoire</UnderlinedText>
            </h2>
            <div className="flex flex-col gap-8 xl:flex-row">
              <div className="flex flex-col gap-4">
                <ul className="space-y-3">
                  <li className="leading-relaxed">
                    <span className="text-orange mt-1 mr-3">1.</span>
                    Création de la facture dans un outil conforme,
                  </li>
                  <li className="leading-relaxed">
                    <span className="text-orange mt-1 mr-3">2.</span>
                    Transmission via une Plateforme Agrée (PA),
                  </li>
                  <li className="leading-relaxed">
                    <span className="text-orange mt-1 mr-3">3.</span>
                    Réception et traitement automatisé de la facture par le client,
                  </li>
                  <li className="leading-relaxed">
                    <span className="text-orange mt-1 mr-3">4.</span>
                    Suivi des statuts : déposée, rejetée, acceptée, payée…
                  </li>
                </ul>
                <Image
                  alt="Visuel de la Plateforme Agrée"
                  className="w-full max-w-[500px] rounded-(--radius-current)"
                  height={500}
                  src={withBasePath('/images/pa/Visuel-PA.webp')}
                  width={500}
                />
                <p className="leading-relaxed">
                  Pour cela, vous devez choisir une plateforme agrée comme zLawyer pour accéder à un
                  logiciel de facturation électronique accepté par l’administration fiscale.
                </p>

                <Button className="w-fit" href="/contact" variant="transparent">
                  Je souhaite être contacté
                </Button>
              </div>
            </div>
          </section>

          <section className="scroll-mt-32 pb-16" id="statuts">
            <h2 className="mb-6 text-3xl font-bold">
              Cycle <UnderlinedText>de vie et statuts</UnderlinedText>
            </h2>
            <p className="mb-8 leading-relaxed">
              Dans le cadre de la réforme, chaque facture électronique suit un cycle de vie
              standardisé, permettant de tracer toutes les étapes, de l’émission à l’archivage. Les
              statuts ci-dessous facilitent la conformité, la gestion et la traçabilité.
            </p>

            <StatusSection
              description="Voici les 4 statuts obligatoires à remonter à l’administration fiscale, tels que définis dans le cadre de la réforme de la facturation électronique :"
              items={STATUTS_OBLIGATOIRES}
              title="Statuts obligatoires"
            />

            <StatusSection
              description="Ces statuts ne sont pas obligatoires réglementairement, mais fortement recommandés pour faciliter le suivi des factures, l’automatisation et la qualité des échanges entre entreprises."
              items={STATUTS_RECOMMANDES}
              title="Statuts recommandés"
            />

            <StatusSection
              description="Ces statuts sont laissés à l’appréciation des acteurs pour enrichir la traçabilité. Ils ne sont pas remontés à la DGFiP, mais sont utiles pour les workflows internes et la gestion client-fournisseur."
              items={STATUTS_LIBRES}
              title="Statuts libres"
            />

            <div className="mt-8 rounded-lg border-l-4 border-blue-400 bg-blue-50 p-6">
              <p className="font-medium text-blue-800">
                📊 Gestion centralisée de ces étapes = gain de temps et conformité garantie.
              </p>
            </div>
          </section>

          <section className="scroll-mt-32 pb-16" id="votre-partenaire-de-confiance">
            <h2 className="mb-6 text-3xl font-bold">
              zLawyer : <UnderlinedText>Votre partenaire de confiance</UnderlinedText>
            </h2>
            <p className="mb-8 leading-relaxed">
              Le logiciel zLawyer est conçu pour répondre aux exigences de la facturation
              électronique.
              <br />
              En optant pour zLawyer vous garantissez une totale conformité aux obligations
              découlant de la réforme sur la facturation électronique tout en bénéficiant d&apos;une
              solution performante et sécurisée.
            </p>
            <Button href="/facturation-electronique-et-zlawyer" variant="transparent">
              Notre solution à la réforme
            </Button>
          </section>

          <div className="my-12">
            <section className="pb-16">
              <div className="p-8 text-center">
                <h2 className="mb-4 text-2xl font-bold">
                  <UnderlinedText>Besoin</UnderlinedText> d&apos;en savoir plus ?
                </h2>
                <Button href="/contact" variant="transparent">
                  Je souhaite être contacté
                </Button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

FacturationElectronique.seo = FACTURATION_ELECTRONIQUE_SEO;
